import { useState } from 'react';
import { Link, useRevalidator } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CASEBUILDER_BASE } from '../../../../../constants/setupRoadmapSteps';
import { useCaseWorkspace } from '../../shared/hooks/useCaseWorkspace';
import { useCaseBuilderStep } from '../../hooks/useCaseBuilderStep';
import CaseWorkspaceHeader from './CaseWorkspaceHeader';
import CaseStepNav from './CaseStepNav';
import OverviewStep from './steps/OverviewStep';
import ClinicalStep from './steps/ClinicalStep';
import BlocksStep from './steps/BlocksStep';
import SlidesStep from './steps/SlidesStep';
import ProtocolPickerModal from '../../../../../components/setup/ProtocolPickerModal';
import ProtocolChangeDialog from '../components/ProtocolChangeDialog';
import styles from '../CaseBuilderEnhanced.module.css';

export default function CaseWorkspaceLayout({
    data,
    name,
    loaderError,
    blockSchema,
    regionSchema,
    stainSchema,
    schemas,
    metadataOptions,
}) {
    const revalidator = useRevalidator();
    const queryClient = useQueryClient();
    const workspace = useCaseWorkspace({ data, name });
    const { step, setStep, steps } = useCaseBuilderStep(name);
    const [showProtocolModal, setShowProtocolModal] = useState(false);
    const [pendingProtocol, setPendingProtocol] = useState(null);
    const [showProtocolChangeDialog, setShowProtocolChangeDialog] = useState(false);
    const [loadPending, setLoadPending] = useState(false);
    const [protocolError, setProtocolError] = useState(null);

    const doc = workspace.doc;
    const caseId = workspace.caseId;
    const hasBlocks = Array.isArray(doc?.blocks) && doc.blocks.length > 0;

    const applyLoad = async (protocol) => {
        if (!protocol?._id) return;
        setProtocolError(null);
        setLoadPending(true);
        try {
            await workspace.loadProtocol(protocol);
        } catch (err) {
            setProtocolError(err?.message || 'Failed to load protocol');
        } finally {
            setLoadPending(false);
        }
    };

    const handleProtocolSelect = (protocol) => {
        setShowProtocolModal(false);
        if (!protocol?._id) return;
        if (hasBlocks) {
            setPendingProtocol(protocol);
            setShowProtocolChangeDialog(true);
            return;
        }
        applyLoad(protocol);
    };

    if (name && !doc) {
        if (revalidator.state === 'loading') {
            return (
                <div className={styles.emptyState}>
                    <h3>Loading case {name}…</h3>
                    <p className={styles.pageSubtitle}>Refreshing after the server came back online.</p>
                </div>
            );
        }
        return (
            <div className={styles.page}>
                <h3 className={styles.pageTitle}>Could not load case {name}</h3>
                <p className={styles.pageSubtitle}>{loaderError || 'The server may have been unavailable.'}</p>
                <p>
                    <button
                        type="button"
                        className={styles.linkButton}
                        onClick={() => {
                            queryClient.removeQueries({ queryKey: ['bdsa-case', name] });
                            revalidator.revalidate();
                        }}
                    >
                        Retry
                    </button>
                    {' · '}
                    <Link to={CASEBUILDER_BASE}>Back to library</Link>
                </p>
            </div>
        );
    }

    const stepProps = {
        doc,
        workspace,
        caseId,
        onGoToStep: setStep,
    };

    return (
        <div className={styles.workspace}>
            <CaseWorkspaceHeader
                doc={doc}
                caseId={caseId}
                workspace={workspace}
            />

            <CaseStepNav step={step} setStep={setStep} steps={steps} doc={doc} />

            {protocolError && (
                <div className={styles.banner} style={{ background: '#ffebe9', color: '#cf222e' }}>
                    {protocolError}
                </div>
            )}

            {step === 'overview' && <OverviewStep {...stepProps} schemas={schemas} />}
            {step === 'clinical' && (
                <ClinicalStep
                    {...stepProps}
                    schemas={schemas}
                    protocolId={workspace.selectedProtocolId}
                />
            )}
            {step === 'blocks' && (
                <BlocksStep
                    {...stepProps}
                    regionSchema={regionSchema}
                    onLoadProtocol={() => setShowProtocolModal(true)}
                    loadPending={loadPending}
                />
            )}
            {step === 'slides' && (
                <SlidesStep
                    doc={doc}
                    setDoc={workspace.setDoc}
                    stainSchema={stainSchema}
                    blockSchema={blockSchema}
                    regionSchema={regionSchema}
                    networkId={caseId}
                    metadataOptions={metadataOptions}
                    affectedItemIds={workspace.affectedItemsAfterBlockRemoval}
                />
            )}

            <ProtocolPickerModal
                isOpen={showProtocolModal}
                onClose={() => setShowProtocolModal(false)}
                onSelect={handleProtocolSelect}
                selectedProtocolId={workspace.selectedProtocolId}
                title="Load protocol"
            />

            <ProtocolChangeDialog
                open={showProtocolChangeDialog}
                onConfirmReplace={() => {
                    const protocol = pendingProtocol;
                    setShowProtocolChangeDialog(false);
                    setPendingProtocol(null);
                    if (protocol) {
                        applyLoad(protocol);
                    }
                }}
                onCancel={() => {
                    setShowProtocolChangeDialog(false);
                    setPendingProtocol(null);
                }}
            />
        </div>
    );
}
